import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { addDays, addMonths, endOfMonth, format, isSameDay,
         startOfMonth, subMonths } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Header from "@/components/Header";
import { api } from "@/lib/api";

export default function CourseSession() {
  const { token: pkgId } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const nav = useNavigate();

  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [monthDate, setMonthDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/course/${pkgId}`, { params: { token } })
      .then((r) => setInfo(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Invalid or expired link"));
  }, [pkgId, token]);

  const earliest = info?.earliest_date ? new Date(info.earliest_date) : null;
  const expiresOn = info?.expires_at ? new Date(info.expires_at) : null;

  useEffect(() => {
    if (earliest && monthDate < startOfMonth(earliest)) setMonthDate(earliest);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info]);

  const days = useMemo(() => {
    const start = startOfMonth(monthDate);
    const end = endOfMonth(monthDate);
    const grid = [];
    // Pad to Monday-start
    const startWeekday = (start.getDay() + 6) % 7;
    for (let i = 0; i < startWeekday; i++) grid.push(null);
    for (let d = 1; d <= end.getDate(); d++) grid.push(new Date(start.getFullYear(), start.getMonth(), d));
    return grid;
  }, [monthDate]);

  const isDayEnabled = (d) => {
    if (!d || !earliest) return false;
    if (d < new Date(new Date().toDateString())) return false;
    if (d < new Date(earliest.toDateString())) return false;
    if (expiresOn && d > expiresOn) return false;
    return true;
  };

  const loadSlots = async (date) => {
    setLoadingSlots(true);
    setSelectedTime(null);
    try {
      const r = await api.get("/slots", {
        params: { date: format(date, "yyyy-MM-dd"), duration: info?.duration_min || 60 },
      });
      setSlots(r.data.slots || []);
    } catch {
      setSlots([]);
    }
    setLoadingSlots(false);
  };

  const pickDate = (d) => {
    setSelectedDate(d);
    loadSlots(d);
  };

  const submit = async () => {
    if (!selectedDate || !selectedTime) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/course/${pkgId}/book`,
        { date: format(selectedDate, "yyyy-MM-dd"), time: selectedTime },
        { params: { token } });
      toast.success(`Session ${r.data.session_n} booked — ${r.data.reference}`);
      nav(`/booking/success?reference=${r.data.reference}`, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not book this session");
      setSubmitting(false);
    }
  };

  if (err) {
    return (
      <>
        <Header />
        <main className="max-w-2xl mx-auto px-6 lg:px-10 pt-10 pb-24">
          <p className="mono-label mb-3">CLAÈ · COURSE SESSION</p>
          <h1 className="font-serif text-3xl">{err}</h1>
          <p className="text-stone-600 mt-4 text-sm">
            If you believe this is a mistake, please contact CLAÈ directly.
          </p>
        </main>
      </>
    );
  }

  if (!info) {
    return (
      <>
        <Header />
        <main className="max-w-2xl mx-auto px-6 lg:px-10 pt-10 pb-24">
          <p className="mono-label">Loading your course details…</p>
        </main>
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="max-w-4xl mx-auto px-6 lg:px-10 pt-10 pb-24" data-testid="course-session-page">
        <p className="mono-label mb-3">CLAÈ · COURSE SESSION</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-light text-stone-900">
          Book Session {info.session_n} of {info.total_sessions}
        </h1>
        <p className="mt-3 text-stone-600 text-sm sm:text-base">
          <strong>{info.course_name}</strong> — Hi {info.client_first_name || "there"}, please choose a
          date and time from your available window below.
        </p>
        <div className="mt-6 border hairline bg-white p-4 sm:p-6 text-sm text-stone-700">
          <div className="flex flex-wrap gap-x-8 gap-y-2">
            <span><span className="mono-label text-stone-500">Earliest</span> · {info.earliest_date}</span>
            <span><span className="mono-label text-stone-500">Expires</span> · {info.expires_at.slice(0, 10)}</span>
            <span><span className="mono-label text-stone-500">Duration</span> · {info.duration_min} min</span>
          </div>
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-8">
          <div>
            <div className="flex items-center justify-between mb-3">
              <button data-testid="prev-month" onClick={() => setMonthDate(subMonths(monthDate, 1))}
                      className="p-2 hover:bg-alt"><ChevronLeft size={18} /></button>
              <p className="mono-label">{format(monthDate, "MMMM yyyy")}</p>
              <button data-testid="next-month" onClick={() => setMonthDate(addMonths(monthDate, 1))}
                      className="p-2 hover:bg-alt"><ChevronRight size={18} /></button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center mono-label text-[10px] text-stone-500 mb-2">
              {["MO", "TU", "WE", "TH", "FR", "SA", "SU"].map((x) => <div key={x}>{x}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {days.map((d, idx) => {
                if (!d) return <div key={idx} />;
                const enabled = isDayEnabled(d);
                const selected = selectedDate && isSameDay(d, selectedDate);
                return (
                  <button
                    key={idx}
                    disabled={!enabled}
                    onClick={() => pickDate(d)}
                    className={`aspect-square text-sm border hairline ${
                      selected ? "bg-stone-900 text-white" :
                      enabled ? "hover:border-stone-900 hover:bg-alt" :
                      "text-stone-300 border-transparent cursor-not-allowed"
                    }`}
                  >{d.getDate()}</button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="mono-label text-stone-500 mb-3">AVAILABLE TIMES</p>
            {!selectedDate && <p className="text-stone-500 text-sm">Select a date to view times.</p>}
            {selectedDate && loadingSlots && <p className="text-stone-500 text-sm">Loading…</p>}
            {selectedDate && !loadingSlots && slots.length === 0 &&
              <p className="text-stone-500 text-sm">No availability on this date.</p>}
            {selectedDate && !loadingSlots && slots.length > 0 && (
              <div className="grid grid-cols-3 gap-2 max-h-[360px] overflow-auto">
                {slots.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSelectedTime(s)}
                    data-testid={`slot-${s}`}
                    className={`border hairline py-2 text-sm ${
                      selectedTime === s ? "bg-stone-900 text-white" : "hover:bg-alt"
                    }`}>{s}</button>
                ))}
              </div>
            )}
            <button
              data-testid="confirm-course-session"
              disabled={!selectedDate || !selectedTime || submitting}
              onClick={submit}
              className="mt-6 w-full bg-stone-900 text-white py-3 mono-label disabled:opacity-40">
              {submitting ? "Booking…" : `Confirm Session ${info.session_n}`}
            </button>
            <p className="text-xs text-stone-500 mt-3">
              No payment is required — the balance for this course is settled in clinic.
            </p>
          </div>
        </div>
      </main>
    </>
  );
}
